import { z } from 'zod'

import { moneySchema, toneSchema } from '@/schemas/common'

/**
 * The two rail blocks that have no hook of their own.
 *
 * `market-now` and `popular-stocks` reuse `IndexSummary` (`@/schemas/market`)
 * and `PopularStocks` (`@/schemas/watchlist`) unchanged — the rail asks the
 * existing hooks for them and re-shapes nothing, so there is nothing to
 * declare here for either.
 *
 * ── Why these live beside the service and not in `src/schemas/` ────────────
 *
 * Every other domain declares its schema in `src/schemas/<domain>.ts`, and
 * this one should too. It does not because this wave's file ownership is
 * `src/features/market-rail/**` and nothing else, so `src/schemas/market-rail.ts`
 * could not be created without reaching outside it. Moving these two objects
 * there is a pure file move whenever someone owns both directories at once.
 */

/**
 * One row of the updates block.
 *
 * `id, title, body, symbol, tone, isIpo`, plus `publishedOn`. (The Market
 * overview used to carry its own date-less copy of this row; it no longer
 * fetches updates at all, so this is the one shape for them.)
 *
 * ── `publishedOn` is a DATE, and that is a backend gap, not a mapping bug ──
 *
 * The V2 render prints a clock time per row — "3:02 PM", "2:40 PM". The wire
 * cannot supply one: `MarketUpdateSerializer.published_at` is a DRF
 * `DateField`, not a `DateTimeField` (`mm-be/api/v5/market/serializers.py:110`,
 * and the generated `components['schemas']['MarketUpdate']` says
 * `Format: date`). There is no time-of-day component in the response for any
 * client-side mapping to recover.
 *
 * So this is the Bikram Sambat date and only the date. Inventing a time from
 * midnight, from `Date.now()` or from the row's position in the list would put
 * a number on screen that the backend never said — which is the one thing a
 * market app must not do.
 *
 * TODO(backend): if the per-row times in the V2 rail are a requirement,
 * `published_at` has to become a `DateTimeField` (or gain a sibling
 * `published_time`) in `mm-be/api/v5/market/serializers.py`. Until then this
 * renders "7 BH" where the design draws "3:02 PM". mm-be is read-only from
 * here; this is a report, not a change.
 */
export const railUpdateSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  body: z.string().min(1),
  /** Doubles as the right-hand label and the logo key — "NABIL", "MARKET". */
  symbol: z.string().min(1),
  tone: toneSchema,
  isIpo: z.boolean().optional(),
  /**
   * Set only when the note is about one listed company (`entity_type ===
   * 'company'`, not an IPO): the row then opens `/stocks/$symbol`. `symbol`
   * alone cannot say it — it is also "MARKET" or a sector name.
   */
  stockSymbol: z.string().min(1).optional(),
  /** The sector's name, set only when the note is about a sector (`entity_type === 'sector'`). */
  sector: z.string().min(1).optional(),
  /** Full BS date, e.g. "2 Bhadra 2083". "—" when the wire date is unparseable. */
  publishedOn: z.string().min(1),
})

export const railUpdatesSchema = z.array(railUpdateSchema)

/** One Top movers row on the Market rail: the ticker, its price and its move. */
export const railMoverSchema = z.object({
  symbol: z.string().min(1),
  ltp: moneySchema,
  percentChange: moneySchema,
})

/** The day's two biggest gainers over its two biggest fallers (the mock's Top movers, 2 + 2). */
export const railMoversSchema = z.object({
  gainers: z.array(railMoverSchema),
  losers: z.array(railMoverSchema),
})

/**
 * NEPSE's 52-week range on the Market rail: today's level, the year's low and high (from
 * `/market/summary/`'s `week52`), and the days they were set, read off the year's history
 * (`/market/indices/NEPSE/history/?range=1Y`). A date is null when that history does not reach it.
 */
export const yearRangeSchema = z.object({
  now: moneySchema,
  low: moneySchema,
  high: moneySchema,
  lowOn: z.string().min(1).nullable(),
  highOn: z.string().min(1).nullable(),
})

export type RailMovers = z.infer<typeof railMoversSchema>
export type YearRange = z.infer<typeof yearRangeSchema>
export type RailUpdate = z.infer<typeof railUpdateSchema>
